import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Eye, Link2Off, Loader2 } from 'lucide-react';
import { API } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

const shareUrl = (token) => `${window.location.origin}/r/${token}`;

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Tenant share link for one inspection. Opening it reuses the live link or
 * creates one (Pro). Without Pro it only shows an existing link so it can be
 * stopped; with no link it hands off to the upgrade dialog instead.
 */
export function useShareReport(inspectionId, { hasPro, onNeedsPro }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const copy = useCallback(async (token, quiet = false) => {
    try {
      await navigator.clipboard.writeText(shareUrl(token));
      if (!quiet) toast.success('Link copied');
      return true;
    } catch {
      return false;
    }
  }, []);

  const create = useCallback(async () => {
    const { data } = await axios.post(`${API}/inspections/${inspectionId}/share`);
    setLink(data);
    return data;
  }, [inspectionId]);

  const start = useCallback(async () => {
    setBusy(true);
    try {
      const { data: current } = await axios.get(`${API}/inspections/${inspectionId}/share`);
      if (current.active) {
        setLink(current);
        setOpen(true);
        if (hasPro && (await copy(current.token, true))) toast.success('Link copied');
        return;
      }
      if (!hasPro) {
        onNeedsPro();
        return;
      }
      const created = await create();
      setOpen(true);
      if (await copy(created.token, true)) toast.success('Share link created and copied');
    } catch (err) {
      if (err.response?.status === 402) onNeedsPro();
      else toast.error("Couldn't load the share link. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  }, [inspectionId, hasPro, onNeedsPro, copy, create]);

  const stop = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/inspections/${inspectionId}/share`);
      setLink({ active: false });
      toast.success('Sharing stopped. The old link no longer works.');
    } catch {
      toast.error("Couldn't stop sharing. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const recreate = async () => {
    setBusy(true);
    try {
      const created = await create();
      if (await copy(created.token, true)) toast.success('New link created and copied');
    } catch (err) {
      if (err.response?.status === 402) onNeedsPro();
      else toast.error("Couldn't create a link. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // Close cleanly if the inspection changes underneath an open dialog.
  useEffect(() => { setOpen(false); setLink(null); }, [inspectionId]);

  const active = !!link?.active;

  const dialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-[520px]" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>Share with tenant</DialogTitle>
          <DialogDescription>
            Anyone with this link can view the report without signing in. They see findings, photos,
            your charge decisions and notes. Dismissed items and your feedback to the AI stay private.
          </DialogDescription>
        </DialogHeader>

        {active ? (
          <div className="space-y-3">
            {!hasPro && (
              <p className="m-0 rounded-md border border-line bg-sunken p-3 text-sm text-ink" role="status">
                This link is paused because Pilot Pro isn't active. Your tenant sees "link not active" until you renew or stop sharing.
              </p>
            )}
            <div className="flex gap-2">
              <input
                readOnly
                value={shareUrl(link.token)}
                onFocus={(e) => e.target.select()}
                aria-label="Share link"
                data-testid="share-link-input"
                className="h-11 min-w-0 flex-1 rounded-md border border-line-strong bg-sunken px-3 font-mono text-sm text-ink"
              />
              <Button type="button" variant="outline" onClick={() => copy(link.token)} data-testid="share-copy-button">
                <Copy strokeWidth={1.5} />Copy
              </Button>
            </div>
            <p className="m-0 flex items-center gap-1.5 text-sm text-ink-muted" data-testid="share-views">
              <Eye className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
              {link.view_count === 0
                ? 'Not opened yet'
                : `Opened ${link.view_count} ${link.view_count === 1 ? 'time' : 'times'}, last on ${formatDate(link.last_viewed_at)}`}
              <span aria-hidden="true">·</span> Created {formatDate(link.created_at)}
            </p>
          </div>
        ) : (
          <p className="m-0 text-sm text-ink-muted">Sharing is off. Links you shared before no longer work.</p>
        )}

        <DialogFooter className="gap-2">
          {active ? (
            <Button type="button" variant="danger" onClick={stop} disabled={busy} data-testid="share-stop-button">
              {busy ? <Loader2 className="animate-spin" /> : <Link2Off strokeWidth={1.5} />}Stop sharing
            </Button>
          ) : hasPro && (
            <Button type="button" onClick={recreate} disabled={busy} data-testid="share-recreate-button">
              {busy && <Loader2 className="animate-spin" />}Create a new link
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { start, busy, dialog };
}
